import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Deck } from '@deckplan.io/common/decks';
import { environment } from '../../environments/environment';
import { Query } from '@deckplan.io/common/filters';
// @ts-ignore
import * as qs from 'qs';
import { Pagination } from '@deckplan.io/common/records';
import { map } from 'rxjs/operators';
import { Card } from '@deckplan.io/common';
import { Mockable } from '../mocks/mockable.decorator';
import { UrlUtils } from '../common/utils/url-utils';

export interface DecksWithTotalCount {
    totalCount: number;
    decks: Deck[];
}
export type DeckWithCardCount = Deck & { cardCount: number };
export type DeckWithCardSizes = Deck & { totalCardCount: number; filteredCardCount: number; totalQuantity: number };

@Injectable({
    providedIn: 'root'
})
@Mockable
export class DecksService {

    private readonly prefix = 'decks';
    private readonly backendUrl = UrlUtils.constructBackendUrl('decks');

    constructor(private readonly http: HttpClient) {}

    private static addQueryAndPage(url: string, query?: Query, pagination?: Pagination): string {
        if (query) {
            url += `?filter=${encodeURIComponent(qs.stringify(query))}`;
        }

        if (pagination) {
            url += query ? '&' : '?';
            url += `pagination=${encodeURIComponent(qs.stringify(pagination))}`;
        }

        return url;
    }

    public getAll(query?: Query, pagination?: Pagination): Observable<DecksWithTotalCount> {
        const url = DecksService.addQueryAndPage(this.backendUrl, query, pagination);

        return this.http.get(url, {observe: 'response'})
            .pipe(
                map(res => ({totalCount: parseInt(res.headers.get('total-count'), 10), decks: res.body as any[]}))
            );
    }

    public get(id: string, query?: Query, pagination?: Pagination): Observable<DeckWithCardSizes> {
        const url = DecksService.addQueryAndPage(`${this.backendUrl}/${id}`, query, pagination);

        return this.http.get(url, {observe: 'response'})
            .pipe(
                map(res => {
                    const deck = res.body as DeckWithCardSizes;
                    deck.totalCardCount = parseInt(res.headers.get('total-count'), 10);
                    deck.filteredCardCount = parseInt(res.headers.get('filtered-count'), 10);
                    deck.totalQuantity = parseInt(res.headers.get('total-quantity'), 10);
                    return deck;
                })
            );
    }

    public createExport(id: string): Observable<{ exportString: string }> {
        const url = `${this.backendUrl}/${id}/export`;
        return this.http.get<{ exportString: string }>(url);
    }

    public getPublic(query?: Query, pagination?: Pagination): Observable<DecksWithTotalCount> {
        const url = DecksService.addQueryAndPage(`${this.backendUrl}/public`, query, pagination);

        return this.http.get(url, {observe: 'response'})
            .pipe(
                map(res => ({totalCount: parseInt(res.headers.get('total-count'), 10), decks: res.body as any[]}))
            );
    }

    public create(deck: Partial<Deck>) {
        return this.http.post<Deck>(`${this.backendUrl}`, deck);
    }

    public update(id: string, updates: Partial<Deck>) {
        return this.http.put<Deck>(`${this.backendUrl}/${id}`, updates);
    }

    public delete(id: string) {
        return this.http.delete<Deck>(`${this.backendUrl}/${id}`);
    }

    public addCard(deckId: string, cardId: string, quantityInDeck = 1) {
        return this.http.post<Deck>(`${environment.apiUrl}/${environment.apiVersion}/${this.prefix}/${deckId}/cards`, {id: cardId, quantityInDeck});
    }

    public addInventoryCard(deckId: string, inventoryCardId: string, quantityInDeck = 1) {
        return this.http.post<Deck>(`${environment.apiUrl}/${environment.apiVersion}/${this.prefix}/${deckId}/inventory`, {inventoryId: inventoryCardId, quantityInDeck});
    }

    public setQuantity(deckId: string, relationId: number, quantityInDeck) {
        return this.http.put<Deck>(`${environment.apiUrl}/${environment.apiVersion}/${this.prefix}/${deckId}/cards/${relationId}`, {quantityInDeck});
    }

    /**
     * Decrements the count of the card in the deck by 1; if the remaining count is 0 when this is finished, removes the card relation entirely from the deck.
     * @param deckId ID of the deck to remove the card from
     * @param relationId Relation of the card to adjust the quantity/remove the relation for.
     */
    public removeCard(deckId: string, relationId: string | number) {
        return this.http.delete<Deck>(`${this.backendUrl}/${deckId}/cards/${relationId}`);
    }

    /**
     * Removes any cards completely from the deck that match the query parameter, marking them as deleted.
     * @param id ID of the deck to remove matching cards from
     * @param query Query construct to match cards in the deck against
     */
    public removeCards(id: string, query: Query) {
        const url = `${this.backendUrl}/${id}/cards?filter=${encodeURIComponent(qs.stringify(query))}`;
        return this.http.delete<Deck>(url);
    }

    public typeCompositions(id: string) {
        const url = `${this.backendUrl}/${id}/type-composition`;
        return this.http.get<any>(url);
    }

    public colorStats(id: string) {
        const url = `${this.backendUrl}/${id}/color-stats`;
        return this.http.get<any>(url);
    }

    public cmcStats(id: string) {
        const url = `${this.backendUrl}/${id}/cmc-stats`;
        return this.http.get<any>(url);
    }

    public getPopularCommanders(count: number) {
        const url = `${this.backendUrl}/popular?count=${count}`;
        return this.http.get<Card[]>(url);
    }

    public addUpvoteToDeck(deckId: string, userId: number) {
        const url = `${this.backendUrl}/${deckId}/upvote/${userId}`;
        return this.http.post<Deck>(url, {});
    }

    public removeUpvoteFromDeck(deckId: string, userId: number) {
        const url = `${this.backendUrl}/${deckId}/upvote/${userId}`;
        return this.http.delete<Deck>(url);
    }

}
